import React, { useState, useEffect } from "react";
import Colors from "../../theme/colors.json";
import { makeStyles } from "@mui/styles";
import { Button, InputAdornment, TextField } from "@mui/material";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import {
  CONFIRMATION_CONTENT,
  CONFIRMATION_TITLE,
  USER_GROUPS,
} from "../../constants/constants";
import { useSelector } from "react-redux";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Fonts from "../../theme/font_size.json";

const useStyles = makeStyles((theme) => ({
  installmentDetailsSection: {
    width: "100%",
    height: "100%",
  },
  installmentDetailsHeadingSection: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
    marginBottom: "1%",
    borderRadius: "0.5vw",
  },
  installmentDetailsHeading: {
    color: theme.palette.secondary.main,
    fontSize: theme.fontSizes.MEDIUM,
    fontWeight: 600,
  },
  installmentDetailsTable: {
    width: "100%",
    borderCollapse: "collapse",
    border: `1px solid ${Colors.BORDER}`,
    marginBottom: "20px",
    "& th, & td": {
      border: `1px solid ${Colors.BORDER}`,
      padding: "8px",
      textAlign: "left",
    },
    background: Colors.WHITE_BACKGROUND,
    borderRadius: "0.6rem",
    fontSize: theme.fontSizes.SMALL,
  },
  installmentDetailsTableHeading: {
    color: theme.palette.secondary.main,
    background: Colors.FORM_HEADING_BG,
    fontWeight: 600,
  },
  tableTextFieldStyles: {
    fontSize: theme.fontSizes.SMALL,
  },
  validationAction: {
    pointerEvents: "none",
  },
  commonBtnStyles: {
    background: Colors.WHITE_BACKGROUND,
    textTransform: "capitalize",
  },
  installmentDetailsTableCell: {
    fontSize: theme.fontSizes.SMALL,
    fontWeight: "normal",
  },
  validationCell: {
    display: "flex",
    flexDirection: "row",
    gap: "6%",
  },
}));

const Date_Feild_Style = {
  fontSize: Fonts[13],
};

interface Installment {
  amount: number;
  dueDate: string;
}

const InstallmentDetails = ({ installmentDetails, setInstallmentDetails }) => {
  const classes = useStyles();
  const { proposalDataRedux } = useSelector((state: any) => ({
    proposalDataRedux: state.proposalData,
  }));
  const { userGroup } = useSelector((state: any) => ({
    userGroup: state.userGroup,
  }));

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const handleYes = () => {
    setConfirmDialogOpen(false);
    setInstallmentDetails((prevState) => ({
      ...prevState,
      validation: true,
    }));
  };

  const handleNo = () => {
    setConfirmDialogOpen(false);
  };

  const handleFieldChanges = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setInstallmentDetails((prevState) => {
      const projectPeriodValue =
        name === "projectPeriod"
          ? parseInt(value, 10)
          : prevState?.projectPeriod || 0;

      const netPremiumValue =
        name === "netPremium"
          ? parseInt(value, 10)
          : prevState?.netPremium || 0;

      let elligibleInstallments = 0;

      if (
        name !== "elligibleInstallments" &&
        projectPeriodValue > 0 &&
        netPremiumValue > 0
      ) {
        elligibleInstallments = Math.floor((projectPeriodValue - 6) / 3) + 1;
      } else if (name === "elligibleInstallments") {
        elligibleInstallments = parseInt(value);
      }

      return {
        ...prevState,
        [name]: parseInt(value, 10),
        elligibleInstallments: elligibleInstallments,
      };
    });
  };

  const handleInstallmentChange = (value, field, id) => {
    let modifiedInstallments = installmentDetails?.installments.map((row) =>
      row.id === id
        ? {
            ...row,
            [field]: value,
          }
        : row
    );

    setInstallmentDetails((prevState) => ({
      ...prevState,
      installments: modifiedInstallments,
    }));
  };

  // Function to calculate installments based on premium amount and period
  const calculateInstallments = () => {
    const premiumAmount = installmentDetails?.netPremium;
    const installmentAmount =
      (premiumAmount * 0.95) / installmentDetails?.elligibleInstallments;
    const extraFirstInstallment = premiumAmount * 0.05; // 5% extra on premium amount for the first installment
    const currentDate = new Date();

    const newInstallments = Array.from(
      { length: installmentDetails?.elligibleInstallments },
      (_, index) => {
        const dueDate = new Date(currentDate);
        dueDate.setMonth(currentDate.getMonth() + index + 1);

        const formattedDueDate = dueDate.toLocaleDateString("en-GB"); // Format as DD/MM/YYYY

        return {
          id: Math.random(),
          amount: Math.round(
            index === 0
              ? installmentAmount + extraFirstInstallment
              : installmentAmount
          ),
          dueDate,
        };
      }
    );

    setInstallmentDetails((prevState) => ({
      ...prevState,
      installments: newInstallments,
    }));
  };

  return (
    <div className={classes.installmentDetailsSection}>
      <ConfirmationDialog
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        title={CONFIRMATION_TITLE}
        content={CONFIRMATION_CONTENT}
        handleYes={handleYes}
        handleNo={handleNo}
      />
      <div className={classes.installmentDetailsHeadingSection}>
        <div className={classes.installmentDetailsHeading}>
          Installment Details
        </div>
      </div>
      <div>
        <table className={classes.installmentDetailsTable}>
          <thead>
            <tr>
              <th className={classes.installmentDetailsTableCell}>
                Project Period
              </th>
              <th>
                <TextField
                  type="number"
                  size="small"
                  name="projectPeriod"
                  value={installmentDetails?.projectPeriod}
                  onChange={handleFieldChanges}
                  InputProps={{
                    className: `${classes.tableTextFieldStyles} ${
                      installmentDetails?.validation
                        ? classes.validationAction
                        : ""
                    }`,
                  }}
                />
              </th>
              <th>
                <Button
                  className={classes.commonBtnStyles}
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={calculateInstallments}
                  disabled={
                    installmentDetails?.validation === true ? true : false
                  }
                >
                  Calculate
                </Button>
              </th>
            </tr>
            <tr>
              <th className={classes.installmentDetailsTableCell}>
                Net Premium
              </th>
              <th>
                <TextField
                  type="number"
                  color="primary"
                  variant="outlined"
                  size="small"
                  name="netPremium"
                  value={installmentDetails?.netPremium}
                  onChange={handleFieldChanges}
                  InputProps={{
                    className: `${classes.tableTextFieldStyles} ${
                      installmentDetails?.validation
                        ? classes.validationAction
                        : ""
                    }`,
                  }}
                />
              </th>
              <th className={classes.validationCell}>
                <Button
                  className={classes.commonBtnStyles}
                  color="primary"
                  variant="outlined"
                  size="small"
                  onClick={() => setConfirmDialogOpen(true)}
                  disabled={
                    installmentDetails?.validation === true ? true : false
                  }
                >
                  Validation
                </Button>
                {userGroup === USER_GROUPS.ADMIN ? (
                  <Button
                    className={classes.commonBtnStyles}
                    onClick={() =>
                      setInstallmentDetails((prevState) => ({
                        ...prevState,
                        validation: false,
                      }))
                    }
                    variant="outlined"
                    size="small"
                    disabled={
                      installmentDetails?.validation === true ? false : true
                    }
                  >
                    Revalidation
                  </Button>
                ) : null}
              </th>
            </tr>

            <tr>
              <th className={classes.installmentDetailsTableCell}>
                No. of eligible Installments
              </th>
              <th>
                <TextField
                  type="number"
                  size="small"
                  name="elligibleInstallments"
                  onChange={handleFieldChanges}
                  value={installmentDetails?.elligibleInstallments}
                  InputProps={{
                    className: `${classes.tableTextFieldStyles} ${
                      installmentDetails?.validation
                        ? classes.validationAction
                        : ""
                    }`,
                  }}
                />
              </th>
            </tr>

            <tr>
              <th className={classes.installmentDetailsTableHeading}>S.No</th>
              <th className={classes.installmentDetailsTableHeading}>Amount</th>
              <th className={classes.installmentDetailsTableHeading}>
                Due date
              </th>
            </tr>
          </thead>
          <tbody>
            {installmentDetails?.installments?.map((installment, index) => (
              <tr key={index}>
                <td>Installment {index + 1}</td>
                <td>
                  <TextField
                    type="number"
                    name="installmentAmount"
                    value={installment?.amount}
                    size="small"
                    onChange={(e) =>
                      handleInstallmentChange(
                        e.target.value,
                        "amount",
                        installment?.id
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                      className: `${classes.tableTextFieldStyles} ${
                        installmentDetails?.validation
                          ? classes.validationAction
                          : ""
                      }`,
                    }}
                  />
                </td>
                <td>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      inputFormat="DD/MM/YYYY"
                      value={installment?.dueDate}
                      onChange={(newValue) =>
                        handleInstallmentChange(
                          newValue,
                          "dueDate",
                          installment?.id
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: "DD/MM/YYYY",
                            style: Date_Feild_Style,
                          }}
                          size="small"
                        />
                      )}
                      className={
                        installmentDetails?.validation
                          ? classes.validationAction
                          : ""
                      }
                    />
                  </LocalizationProvider>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InstallmentDetails;
